import { gql } from '@apollo/client';

// Tag Management — the `tags` collection (advisory metadata mirror of entity
// `tags: string[]`; one doc per (account, team, type, name)).
//
// TAG_ENTITY_TYPES values are the GraphQL enum KEYS (SCREEN | ASSET | PLAYLIST)
// per the repo's enum-casing rule; Mongo stores lowercase.
//
// NOTE: Inlined fields (no fragments) — the `tags` resolver projects via
// extractFields/toMongoProjections, which does not resolve fragment spreads
// (same constraint as provisioning-templates.ts: fragments would make Mongo
// return only _id with every other field null).
//
// renameTag / deleteTag(s) return the doc immediately with status
// RENAMING/DELETING set — the string sweep runs server-side in the background;
// refetch TAGS_QUERY until status clears.

export const TAGS_QUERY = gql`
  query Tags(
    $teamId: String
    $type: TAG_ENTITY_TYPES!
    $after: String
    $first: Int
    $query: QueryTagInput
    $sort: TagSort
  ) {
    tags(teamId: $teamId, type: $type, after: $after, first: $first, query: $query, sort: $sort) {
      page {
        edges {
          cursor
          node {
            _id
            name
            normalizedName
            type
            teamId
            color
            description
            usageCount
            engageUsageCount
            usedOnEngage
            ownUsageCount
            rootTargetUsageCount
            itemTargetUsageCount
            status
            sweepStartedAt
            sweepError
            createdAt
            lastUpdatedDate
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
      }
      pageData {
        limit
        offset
      }
    }
  }
`;

// Paged, merged suggestion feed for the tag pickers (rule builders, target-tag
// inputs, EditTagsDialog, Assets sidebar). One row per NAME across the requested
// universes — `types` carries every universe the name exists in (badge data),
// in the requested order. Sort: name asc (byte order) by default; recentFirst
// flips to lastUpdatedDate desc (Assets-sidebar recency listing). `search` is a
// case-insensitive contains match on normalizedName, so typing finds tags on
// pages not yet loaded.
export const TAG_SUGGESTIONS_QUERY = gql`
  query TagSuggestions(
    $teamId: String
    $types: [TAG_ENTITY_TYPES!]!
    $after: String
    $first: Int
    $search: String
    $engage: Boolean
    $recentFirst: Boolean
  ) {
    tagSuggestions(
      teamId: $teamId
      types: $types
      after: $after
      first: $first
      search: $search
      engage: $engage
      recentFirst: $recentFirst
    ) {
      page {
        edges {
          cursor
          node {
            name
            types
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
      pageData {
        limit
        offset
      }
    }
  }
`;

export const TAG_SEARCH_QUERY = gql`
  query TagSearch($teamId: String, $type: TAG_ENTITY_TYPES!, $term: String!, $limit: Int) {
    tagSearch(teamId: $teamId, type: $type, term: $term, limit: $limit) {
      _id
      name
      normalizedName
      type
      color
      usageCount
      status
    }
  }
`;

export const TAG_ENTITIES_QUERY = gql`
  query TagEntities(
    $teamId: String
    $type: TAG_ENTITY_TYPES!
    $name: String!
    $limit: Int
    $sortDirection: SortDirection
    $offset: Int
  ) {
    tagEntities(
      teamId: $teamId
      type: $type
      name: $name
      limit: $limit
      sortDirection: $sortDirection
      offset: $offset
    ) {
      total
      entities {
        _id
        name
        path
        matchedOnTags
        matchedOnTargetTags
        matchedItemNames
        matchedItemCount
      }
    }
  }
`;

// DISTINCT entities referencing ANY of the names (bulk-delete warning) — an
// entity carrying several of the selected tags counts once, unlike summing
// the rows' usageCounts, which double-counts multi-tagged entities.
export const TAG_ENTITIES_COUNT_QUERY = gql`
  query TagEntitiesCount($teamId: String, $type: TAG_ENTITY_TYPES!, $names: [String!]!) {
    tagEntitiesCount(teamId: $teamId, type: $type, names: $names)
  }
`;

export const CREATE_TAG = gql`
  mutation CreateTag($teamId: String, $payload: CreateTagInput!) {
    createTag(teamId: $teamId, payload: $payload) {
      _id
      name
      normalizedName
      type
      color
      description
      usageCount
      status
    }
  }
`;

export const UPDATE_TAG = gql`
  mutation UpdateTag($teamId: String, $payload: UpdateTagInput!) {
    updateTag(teamId: $teamId, payload: $payload) {
      _id
      name
      color
      description
      status
    }
  }
`;

export const RENAME_TAG = gql`
  mutation RenameTag($teamId: String, $payload: RenameTagInput!) {
    renameTag(teamId: $teamId, payload: $payload) {
      _id
      name
      status
    }
  }
`;

export const DELETE_TAG = gql`
  mutation DeleteTag($teamId: String, $payload: DeleteTagInput!) {
    deleteTag(teamId: $teamId, payload: $payload) {
      _id
      name
      status
    }
  }
`;

export const RETRY_TAG_SWEEP = gql`
  mutation RetryTagSweep($teamId: String, $_id: String!) {
    retryTagSweep(teamId: $teamId, _id: $_id) {
      _id
      name
      status
      sweepStartedAt
      sweepError
    }
  }
`;

export const DELETE_TAGS = gql`
  mutation DeleteTags($teamId: String, $payload: DeleteTagsInput!) {
    deleteTags(teamId: $teamId, payload: $payload) {
      _id
      name
      status
    }
  }
`;

/** Tag universe values as the GraphQL enum KEYS the API expects. */
export const TAG_ENTITY_TYPE_KEYS = ['SCREEN', 'ASSET', 'PLAYLIST'] as const;
export type TagEntityTypeKey = (typeof TAG_ENTITY_TYPE_KEYS)[number];

export interface TagNode {
  _id: string;
  name: string;
  normalizedName?: string | null;
  type: TagEntityTypeKey;
  teamId?: string | null;
  color?: string | null;
  description?: string | null;
  usageCount?: number | null;
  engageUsageCount?: number | null;
  usedOnEngage?: boolean | null;
  /** PLAYLIST universe only — split of usageCount by source (see tag.model.ts):
   *  own = playlists whose tags proper carry the name; rootTarget = the
   *  Playlist Options default; itemTarget = an item's Asset Options tags.
   *  The target counters drive the manager's location badges. */
  ownUsageCount?: number | null;
  rootTargetUsageCount?: number | null;
  itemTargetUsageCount?: number | null;
  status?: 'RENAMING' | 'DELETING' | null;
  sweepStartedAt?: string | null;
  sweepError?: string | null;
  createdAt?: string | null;
  lastUpdatedDate?: string | null;
}

export interface TagsQueryResult {
  tags: {
    page: {
      edges: Array<{ cursor: string; node: TagNode }>;
      pageInfo: {
        endCursor: string | null;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
        startCursor: string | null;
      };
    };
    pageData: { limit: number; offset: number };
  };
}

export interface TagSearchResult {
  tagSearch: TagNode[];
}

/** One suggestion row — a NAME plus every requested universe it exists in. */
export interface TagSuggestionNode {
  name: string;
  types: TagEntityTypeKey[];
}

export interface TagSuggestionsQueryResult {
  tagSuggestions: {
    page: {
      edges: Array<{ cursor: string; node: TagSuggestionNode }>;
      pageInfo: {
        endCursor: string | null;
        hasNextPage: boolean;
      };
    };
    pageData: { limit: number; offset: number };
  };
}

export interface TagEntityRef {
  _id: string;
  name?: string | null;
  /** PLAYLIST rows only — folder path, shown under item rows as path + name */
  path?: string | null;
  /** PLAYLIST rows only — where the tag lives on this playlist (union rule).
   *  matchedItemNames is capped at 20 (the tooltip depth); matchedItemCount is
   *  the TRUE total behind the "+N" numbers. */
  matchedOnTags?: boolean | null;
  matchedOnTargetTags?: boolean | null;
  matchedItemNames?: string[] | null;
  matchedItemCount?: number | null;
}

export interface TagEntitiesResult {
  tagEntities: {
    total: number;
    entities: TagEntityRef[];
  };
}
